import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { MessageService } from 'primeng/api';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { captureMessage } from '@sentry/angular';
import { AuthenticationService } from '@services/authentication/authentication.service';
import { SummitIdObject } from '@summit/models';
import { HistoryService } from '@summit/services';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private messageService: MessageService,
    private history: HistoryService
  ) {}

  intercept(request: HttpRequest<SummitIdObject>, next: HttpHandler): Observable<HttpEvent<SummitIdObject>> {
    return next.handle(request).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse) {
          switch (err.status) {
            case 0:
              //Think this is timeout
              break;
            case 200:
              break;
            case 401:
              // auto logout if 401 response returned from api
              captureMessage('Http: 401 Unauthorized', {
                extra: {
                  request: { method: request.method, url: request.url, params: request.params?.toString() ?? '' },
                  error: err
                }
              });
              this.authService.logout();
              if (!this.router.url.startsWith('/login')) {
                this.router.navigate(['login'], {
                  queryParams: { returnUrl: this.router.url }
                });
              }
              break;
            case 400:
              // console.log('ErrorInterceptor: Got Error 400 - Leaving on stack', err);
              // var apiError:ApiError = new ApiError(err,request);
              console.log('request', request, err);
              if (request.method !== 'PUT' && request.method !== 'POST') {
                /*
                rg4js('send', {
                  error: err,
                  customData: {
                    url: err.url,
                    body: request?.serializeBody()
                  },
                  tags: ['API', `${err.status}`]
                });
                */
              }
              if (request.method === 'POST' && request.url.includes('/Batch')) {
                this.messageService.add({
                  key: 'notification',
                  severity: 'error',
                  summary: `Sorry, we received the following error:\n${err.error.message ?? err.error}`,
                  closable: true
                });
              }
              if (request.method === 'POST' && !request.url.includes('/Batch')) {
                const errorMessage =
                  err.error?.message ??
                  err.error?.title ??
                  'An error occurred processing your request. Please try again.';
                console.log('ErrorInterceptor: Got Error 400 (POST)', errorMessage);
                this.messageService.add({
                  key: 'notification',
                  severity: 'error',
                  summary: `Sorry, we received the following error:\n${errorMessage}`,
                  closable: true
                });
                captureMessage('Http: 400 Bad Request', {
                  extra: {
                    request: { method: request.method, url: request.url, params: request.params?.toString() ?? '' },
                    error: err
                  }
                });
              }
              if (request.method === 'PUT') {
                const errorMessage =
                  err.error?.message ??
                  Object.keys(err.error?.model ?? {})
                    .map((key) => `${key}: ${err.error.model[key].join(', ')}`)
                    .join('\n');
                console.log('ErrorInterceptor: Got Error 400', errorMessage);
                this.messageService.add({
                  key: 'notification',
                  severity: 'error',
                  summary: `Sorry, we received the following error:\n${errorMessage}`,
                  closable: true
                });
              }
              if (request.method === 'GET') {
                this.messageService.add({
                  key: 'notification',
                  severity: 'error',
                  summary: 'Sorry, we had an issue retrieving your data. Please refresh the page and try again.',
                  closable: true
                });
                captureMessage('Http: 400 Bad Request', {
                  extra: {
                    request: { method: request.method, url: request.url, params: request.params?.toString() ?? '' },
                    error: err
                  }
                });
              }

              /*
              Logic moved to APIError Constructor to parse
              */
              break;
            case 403:
              captureMessage('Http: 403 Unauthorized', {
                extra: {
                  request: { method: request.method, url: request.url, params: request.params?.toString() ?? '' },
                  error: err
                }
              });
              if (!this.router.url.startsWith('/dashboard')) {
                console.log('ErrorInterceptor: Got Error 403 - Navigating to Dashboard', err);
                this.router.navigate(['dashboard']);
              }
              break;
            case 404:
              // Handled by Router before intercepted via redirect wildcard
              console.log('ErrorInterceptor: Got Error 404 - Leaving on stack', err);
              if (
                err.url.toLocaleLowerCase().indexOf('/selectable') === -1 &&
                err.url.toLocaleLowerCase().indexOf('/files/tenant') === -1 &&
                err.url.toLocaleLowerCase().indexOf('/downloadfile') === -1  // Don't redirect for downloadFile endpoint
              ) {
                this.messageService.add({
                  key: 'notification',
                  severity: 'error',
                  summary: 'Sorry, we could not find your data. Please refresh the page and try again.',
                  closable: true,
                  life: 3000
                });
                //TODO: catch cross Tenant Links and find right tenant
                console.log('Going to history back with fall back to dashboard');
                this.history.back('/Dashboard');
              } else {
                this.messageService.add({
                  key: 'notification',
                  severity: 'warn',
                  summary: 'Sorry, we had trouble finding some of your data. Please validate your data before saving.',
                  closable: true
                });
              }

              break;
            case 408:
              break;
            case 500:
              if (request.method === 'POST' || request.method === 'PUT') {
                const errorMessage =
                  err.error?.message ??
                  err.error?.title ??
                  'An unexpected server error occurred. Please try again or contact support.';
                this.messageService.add({
                  key: 'notification',
                  severity: 'error',
                  summary: `Sorry, we received the following error:\n${errorMessage}`,
                  closable: true
                });
                captureMessage('Http: 500 Internal Server Error', {
                  extra: { request: { method: request.method, url: request.url, params: request.params?.toString() ?? '' }, error: err }
                });
                break;
              }
            default:
              console.log(`Status: ${err.status} on ${err.url}`, err);
              if (
                err.status >= 500 &&
                err.status < 600 &&
                (err.url.indexOf('ChangeToken') >= 0 || err.url.indexOf('GenerateToken') >= 0)
              ) {
                this.authService.logout();
                this.router.navigate(['login']);
                this.messageService.add({
                  key: 'notification',
                  life: 3000,
                  summary: 'Authentication Error. Logging out.',
                  severity: 'error',
                  closable: true
                });
              } else {
                console.log(`ErrorInterceptor: Got Error Unknown Error: ${err.status} - Leaving on stack`, err);
                this.messageService.add({
                  key: 'notification',
                  life: 3000,
                  severity: 'error',
                  summary: this.buildString(err)
                });
              }
              break;
          }
        }
        return throwError(() => err);
      })
    );
  }

  buildString(err: HttpErrorResponse): string {
    let response = `An error has occurred.\nSupport has been notified.\n`;
    response += `If needed, send additional information to support@ebopro.com :\n`;
    response += 'Error Type: HttpError Response\n';
    response += `Error Code: ${err.status}\n`;
    response += `Error Message: ${err.message}\n`;
    return response;
  }
}
